import { useState, useEffect, useRef } from 'react'
import {
  Box,
  Paper,
  Typography,
  Button,
  Tabs,
  Tab,
  IconButton,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { AnimatedBox } from './AnimatedBox'
import { PageGuideButton } from '../onboarding/PageGuideButton'

/**
 * DetailPageLayout - Reusable layout for detail pages (campaigns, clients, etc.)
 * Provides consistent structure with header, actions, and tabbed content
 * 
 * @param {string} title - Page title
 * @param {string} subtitle - Optional subtitle
 * @param {ReactNode} actions - Action buttons (edit, delete, etc.)
 * @param {Array} tabs - Array of tab configs: [{ label, content, badge }]
 * @param {number} defaultTab - Default active tab index
 * @param {number} [activeTab] - Controlled active tab (when provided with onTabChange)
 * @param {Function} [onTabChange] - Called when tab changes (for controlled mode)
 * @param {Function} onBack - Callback for back button
 * @param {ReactNode} headerContent - Additional header content
 * @param {ReactNode} children - Content to render (if not using tabs)
 */
export const DetailPageLayout = ({
  title,
  subtitle = null,
  guideSurfaceId = null,
  actions = null,
  tabs = [],
  defaultTab = 0,
  activeTab: controlledActiveTab,
  onTabChange: controlledOnTabChange,
  onBack = null,
  headerContent = null,
  children = null,
}) => {
  const [internalTab, setInternalTab] = useState(defaultTab)
  const isControlled = controlledActiveTab !== undefined && controlledOnTabChange != null
  const activeTab = isControlled ? controlledActiveTab : internalTab
  const activeTabContentRef = useRef(null)

  useEffect(() => {
    if (!isControlled) queueMicrotask(() => setInternalTab(defaultTab))
  }, [defaultTab, isControlled])

  useEffect(() => {
    if (tabs.length > 0 && activeTabContentRef.current) {
      activeTabContentRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [activeTab, tabs.length])

  const handleTabChange = (event, newValue) => {
    if (isControlled) {
      controlledOnTabChange(newValue)
    } else {
      setInternalTab(newValue)
    }
  }

  return (
    <AnimatedBox sx={{ pb: 4 }}>
      {/* Header Section - clean hierarchy */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 3 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {onBack && (
              <IconButton
                onClick={onBack}
                size="small"
                sx={{ mb: 1, ml: -0.5 }}
                aria-label="Go back"
              >
                <ArrowBackIcon fontSize="small" />
              </IconButton>
            )}
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 0.5 }}>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: headerContent ? 2 : 0 }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          {(guideSurfaceId || actions) && (
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', alignSelf: 'flex-start' }}>
              {guideSurfaceId ? <PageGuideButton surfaceId={guideSurfaceId} /> : null}
              {actions}
            </Box>
          )}
        </Box>
        {headerContent && (
          <Box sx={{ width: '100%', mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
            {headerContent}
          </Box>
        )}
      </Paper>

      {/* Tabs Navigation */}
      {tabs.length > 0 && (
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            mb: 0,
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              minHeight: 48,
            },
          }}
        >
          {tabs.map((tab, index) => (
            <Tab
              key={index}
              value={index}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {tab.label}
                  {tab.badge && (
                    <Box
                      sx={{
                        bgcolor: 'primary.main',
                        color: 'white',
                        borderRadius: '12px',
                        px: 1,
                        py: 0.25,
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        minWidth: 20,
                        textAlign: 'center',
                      }}
                    >
                      {tab.badge}
                    </Box>
                  )}
                </Box>
              }
            />
          ))}
        </Tabs>
      )}

      {/* Content Section */}
      {tabs.length > 0 ? (
        <Box sx={{ pt: 3 }}>
          {tabs.map((tab, index) => (
            <Box
              key={index}
              ref={activeTab === index ? activeTabContentRef : undefined}
              sx={{
                display: activeTab === index ? 'block' : 'none',
              }}
            >
              {tab.content}
            </Box>
          ))}
        </Box>
      ) : (
        children
      )}
    </AnimatedBox>
  )
}
